import React, { useCallback, useMemo, useRef } from "react";
import { MapPin } from "lucide-react";
import {
  GoogleMap,
  Marker,
  Circle,
  Polygon,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER_ID } from "../../../../lib/googleMapsConfig";
import { isPolygonSimple } from "../../../../lib/mapUtils";
import "./JobLocationMap.css";

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // Continental US center
const DEFAULT_ZOOM = 4;
const PIN_ZOOM = 15;
const mapContainerStyle = { width: "100%", height: "100%" };

export default function JobLocationMap({
  latitude,
  longitude,
  editable,
  onChange,
  mode = "point",
  radius,
  polygonPoints,
  onRadiusChange,
  onPolygonChange,
  resetKey,
  movingRegion,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const circleRef = useRef(null);
  const polygonRef = useRef(null);
  const pathListenersRef = useRef([]);

  const isPolygonMode = mode === "polygon";
  const points = polygonPoints || [];
  const hasValidPolygon = points.length >= 3;

  // Holds the latest points without becoming a render dependency: the
  // Polygon below is only ever seeded from this at mount (see
  // handlePolygonLoad), never re-synced via a reactive `paths` prop.
  const pointsRef = useRef(points);
  pointsRef.current = points;

  const hasLocation = latitude != null && longitude != null;

  // GoogleMap treats `center`/`zoom` as controlled props and calls
  // map.setCenter()/setZoom() whenever they change. If these were derived
  // from the polygon's live points, dragging the whole shape (or editing a
  // vertex) would recompute the centroid on every update and re-pan the map
  // to follow it — compounding on top of the shape's own native drag
  // translation and making it look like it moves far faster than the
  // cursor. So these are frozen: only recomputed on a deliberate reset
  // (resetKey bump on mode switch/cancel/clear/initial load), from
  // pointsRef's snapshot at that moment, never reactively from `points`.
  const center = useMemo(() => {
    if (isPolygonMode && pointsRef.current.length > 0) {
      const latSum = pointsRef.current.reduce((sum, p) => sum + p.lat, 0);
      const lngSum = pointsRef.current.reduce((sum, p) => sum + p.lng, 0);
      return {
        lat: latSum / pointsRef.current.length,
        lng: lngSum / pointsRef.current.length,
      };
    }
    return hasLocation ? { lat: latitude, lng: longitude } : DEFAULT_CENTER;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, isPolygonMode, hasLocation, latitude, longitude]);

  const zoom = useMemo(() => {
    if (isPolygonMode) {
      return pointsRef.current.length > 0
        ? 16
        : hasLocation
        ? PIN_ZOOM
        : DEFAULT_ZOOM;
    }
    return hasLocation ? PIN_ZOOM : DEFAULT_ZOOM;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, isPolygonMode, hasLocation]);

  const handleMapClick = useCallback(
    (e) => {
      if (!editable) return;
      const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      if (isPolygonMode) {
        if (!hasValidPolygon && onPolygonChange) {
          onPolygonChange([...points, point]);
        }
        return;
      }
      if (onChange) onChange({ latitude: point.lat, longitude: point.lng });
    },
    [
      editable,
      isPolygonMode,
      hasValidPolygon,
      onPolygonChange,
      points,
      onChange,
    ]
  );

  const handleMarkerDragEnd = useCallback(
    (e) => {
      if (!onChange) return;
      onChange({ latitude: e.latLng.lat(), longitude: e.latLng.lng() });
    },
    [onChange]
  );

  const handleCircleLoad = useCallback(
    (circle) => {
      circleRef.current = circle;
      circle.addListener("radius_changed", () => {
        if (onRadiusChange) onRadiusChange(circle.getRadius());
      });
    },
    [onRadiusChange]
  );

  const clearPathListeners = () => {
    pathListenersRef.current.forEach((listener) => listener.remove());
    pathListenersRef.current = [];
  };

  const emitPolygonPath = useCallback(() => {
    if (!polygonRef.current || !onPolygonChange) return;
    const path = polygonRef.current
      .getPath()
      .getArray()
      .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
    onPolygonChange(path);
  }, [onPolygonChange]);

  const handlePolygonLoad = useCallback(
    (polygon) => {
      polygonRef.current = polygon;
      // Seed the path once from whatever points were current at mount time.
      // We deliberately never feed `points` back in as a reactive `paths`
      // prop afterward — @react-google-maps/api's paths-updater calls
      // polygon.setPaths() on every prop change, which internally clears
      // and re-inserts the path one point at a time; our own insert_at
      // listener would catch those partial rebuild steps and write a
      // truncated shape back into React state. Editing is one-way
      // (native drag/insert/remove -> React state) from here on; a
      // deliberate reset is done via remounting through `resetKey`.
      polygon.setPath(pointsRef.current);
      clearPathListeners();
      const path = polygon.getPath();
      pathListenersRef.current = [
        path.addListener("insert_at", emitPolygonPath),
        path.addListener("remove_at", emitPolygonPath),
        path.addListener("set_at", emitPolygonPath),
        polygon.addListener("dragend", emitPolygonPath),
      ];
    },
    [emitPolygonPath]
  );

  const handlePolygonUnmount = useCallback(() => {
    clearPathListeners();
    polygonRef.current = null;
  }, []);

  if (loadError) {
    return (
      <div className="job-location-map job-location-map--empty">
        <MapPin size={32} className="job-location-map__icon" />
        <p className="job-location-map__title">Unable to load Google Maps</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="job-location-map job-location-map--empty">
        <p className="job-location-map__title">Loading map...</p>
      </div>
    );
  }

  const polygonIsSimple = hasValidPolygon && isPolygonSimple(points);

  return (
    <div className="job-location-map">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={zoom}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      >
        {!isPolygonMode && hasLocation && (
          <Marker
            position={{ lat: latitude, lng: longitude }}
            draggable={editable}
            onDragEnd={handleMarkerDragEnd}
          />
        )}
        {!isPolygonMode && hasLocation && radius != null && (
          <Circle
            center={{ lat: latitude, lng: longitude }}
            radius={radius}
            editable={editable}
            draggable={false}
            onLoad={handleCircleLoad}
            options={{
              fillColor: "#2563eb",
              fillOpacity: 0.15,
              strokeColor: "#2563eb",
              strokeWeight: 1,
            }}
          />
        )}

        {isPolygonMode && hasValidPolygon && (
          <Polygon
            key={resetKey}
            // editable (vertex drag/insert/remove) and draggable (whole-shape
            // move) are kept mutually exclusive: Google Maps' vertex-edit and
            // shape-drag handlers conflict when both are active at once,
            // producing an erratic, oversized drag delta.
            editable={editable && !movingRegion}
            draggable={editable && !!movingRegion}
            onLoad={handlePolygonLoad}
            onUnmount={handlePolygonUnmount}
            options={{
              fillColor: polygonIsSimple ? "#2563eb" : "#dc2626",
              fillOpacity: 0.2,
              strokeColor: polygonIsSimple ? "#2563eb" : "#dc2626",
              strokeWeight: 2,
            }}
          />
        )}
        {isPolygonMode && !hasValidPolygon && points.length > 0 && (
          <>
            <Polyline
              path={points}
              options={{ strokeColor: "#2563eb", strokeWeight: 2 }}
            />
            {points.map((p, i) => (
              <Marker key={`polygon-point-${i}`} position={p} />
            ))}
          </>
        )}
      </GoogleMap>
      {!isPolygonMode && !hasLocation && (
        <div className="job-location-map__overlay">
          <MapPin size={32} className="job-location-map__icon" />
          <p className="job-location-map__title">No location set</p>
          {editable && (
            <p className="job-location-map__subtitle">
              Click the map or enter coordinates below to set the job location
            </p>
          )}
        </div>
      )}
      {isPolygonMode && points.length === 0 && (
        <div className="job-location-map__overlay">
          <MapPin size={32} className="job-location-map__icon" />
          <p className="job-location-map__title">No region set</p>
          {editable && (
            <p className="job-location-map__subtitle">
              Click the map to place at least 3 points defining the region
            </p>
          )}
        </div>
      )}
    </div>
  );
}
